/**
 * Queues Tab — ABR filter management for community admins.
 *
 * Displays a list of ABR filters with create, edit, and delete actions.
 * Uses DataList pattern consistent with ModerationQueueTable.
 */

import type { AbrFilter } from '@moderation/domain/AbrFilter';
import { useAbrFilters } from '@moderation/hooks';
import { Button, ButtonType, Heading } from '@soteria/design-system-ve';
import { useState } from 'react';

import { AbrFilterDeleteDialog } from './AbrFilterDeleteDialog';
import { AbrFilterFormDialog } from './AbrFilterFormDialog';
import { AbrFilterTable } from './AbrFilterTable';
import * as styles from './QueuesTab.css';

interface QueuesTabProps {
  communityId: string;
}

export function QueuesTab({ communityId }: QueuesTabProps) {
  const { filters, loading, errors, refetch } = useAbrFilters(communityId);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editFilter, setEditFilter] = useState<AbrFilter | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AbrFilter | null>(null);

  if (errors && errors.length > 0 && !loading) {
    return (
      <div role="alert">
        <Heading level="h2">Error loading queue filters</Heading>
        <Button buttonType={ButtonType.Primary} onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <section aria-label="Queue filter management">
      <div className={styles.tabHeader}>
        <Heading level="h2">Queue Filters</Heading>
        <Button buttonType={ButtonType.Primary} onClick={() => setIsCreateOpen(true)}>
          Create Filter
        </Button>
      </div>

      <AbrFilterTable filters={filters} isLoading={loading} onDelete={setDeleteTarget} onEdit={setEditFilter} />

      <AbrFilterFormDialog communityId={communityId} filter={null} isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />

      {editFilter && <AbrFilterFormDialog communityId={communityId} filter={editFilter} isOpen={Boolean(editFilter)} onClose={() => setEditFilter(null)} />}

      {deleteTarget && (
        <AbrFilterDeleteDialog communityId={communityId} filter={deleteTarget} isOpen={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} />
      )}
    </section>
  );
}
